<script setup>
import { ref, watch } from 'vue';
import { maxValue, minValue, requiredIf } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import FormInput from '../../atoms/form/FormInput.vue';
import { customFieldProps } from '../../../utils/custom-fields';
import CustomFieldWrapper from './CustomFieldWrapper.vue';
import FormMessage from '../../atoms/form/FormMessage.vue';
import { isValidNumberFormat, validateIf } from '../../../utils/validators';

const props = defineProps({ ...customFieldProps });
const model = defineModel({
    type: Number,
});

const fieldValue = ref(Number.isNaN(model.value) ? null : model.value);

const rules = {
    fieldValue: {
        required: requiredIf(() => props.required),
        doesNotEndWithComma: value => !value?.toString().endsWith(','),
        isValidNumberFormat,
        minValue: validateIf(props.minValue !== null, minValue(props.minValue)),
        maxValue: validateIf(props.maxValue !== null, maxValue(props.maxValue)),
    },
};

const v$ = useVuelidate(rules, { fieldValue }, { $registerAs: `custom-field-${props.id}` });

watch(fieldValue, (newValue) => {
    v$.value.$touch();
    model.value = v$.value.$invalid ? null : parseFloat(newValue.replace('.', '').replace(',', '.'));
});
</script>

<template>
    <CustomFieldWrapper
        :id="id"
        :error="v$.$error"
        :label="label"
        :required="required"
        :status="v$.$error ? 'error' : ''"
        data-qa="custom field number"
    >
        <FormInput
            :id="`custom-field-${id}`"
            v-model="fieldValue"
            :placeholder="placeholder"
            :name="`custom-field-${id}`"
        />
        <FormMessage
            v-if="v$.$error"
            type="error"
            data-qa="custom field number error message"
        >
            <template v-if="v$.fieldValue.required.$invalid">
                Bitte gib eine Zahl an.
            </template>
            <template v-else-if="v$.fieldValue.minValue.$invalid">
                Die niedrigste erlaubte Zahl ist {{ props.minValue }}.
            </template>
            <template v-else-if="v$.fieldValue.maxValue.$invalid">
                Die höchste erlaubte Zahl ist {{ props.maxValue }}.
            </template>
            <template v-else-if="v$.fieldValue.doesNotEndWithComma.$invalid">
                Die Zahl darf nicht mit einem Komma enden.
            </template>
            <template v-else-if="v$.fieldValue.isValidNumberFormat.$invalid">
                Dies ist kein gültiges Zahlenformat. Bitte nutze Punkte als Tausender-Trennzeichen und ein Komma für
                Dezimal-Trennzeichen.
            </template>
        </FormMessage>
    </CustomFieldWrapper>
</template>
